import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

export const PurchaseSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchaseDetails, setPurchaseDetails] = useState<any>(null);
  const [autoLoggingIn, setAutoLoggingIn] = useState(false);
  const [loadingStep, setLoadingStep] = useState(1);
  const [magicLinkUrl, setMagicLinkUrl] = useState<string | null>(null);
  const [retryAttempted, setRetryAttempted] = useState(false);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      handleAutoLogin(sessionId);
    } else {
      setError('No session ID provided');
      setLoading(false);
    }
  }, [sessionId]);

  const handleAutoLogin = async (sessionId: string) => {
    try {
      setAutoLoggingIn(true);
      setLoadingStep(1); // Account created
      
      // Step 1: Get session token for immediate login
      const response = await fetch(`/api/auth/auto-login/stripe?session_id=${sessionId}`);
      
      if (response.ok) {
        const data = await response.json();
        setLoadingStep(2); // Logging in
        
        // Session cookie should be set by the API response
        // No need to store in sessionStorage
        
        setLoadingStep(3); // Preparing content
        
        // Show brief success message then redirect
        setPurchaseDetails({
          sessionId,
          status: 'completed',
          contentTitle: data.contentTitle,
          autoLogin: true,
          contentId: data.contentId,
          purchaseType: data.purchaseType
        });
        
        setLoading(false);
        setAutoLoggingIn(false);
        
        // Auto-redirect to PDF viewer for content purchases, portal for other types
        setTimeout(() => {
          if (data.purchaseType === 'content' && data.contentId) {
            navigate(`/client/portal?tab=resources&viewContent=${data.contentId}`);
          } else if (data.purchaseType === 'booking') {
            navigate('/client/portal?tab=appointments');
          } else {
            navigate('/client/portal?tab=resources');
          }
        }, 3000);
        
      } else {
        // Enhanced fallback - get magic link for immediate access
        await handleAutoLoginFailure(sessionId);
      }
    } catch (error) {
      console.error('Auto-login failed:', error);
      await handleAutoLoginFailure(sessionId);
    }
  };

  const handleAutoLoginFailure = async (sessionId: string) => {
    try {
      // Try to get magic link for immediate access
      const magicResponse = await fetch(`/api/auth/magic-link?session_id=${sessionId}`);
      
      if (magicResponse.ok) {
        const magicData = await magicResponse.json();
        setMagicLinkUrl(magicData.magicLink);
        
        setPurchaseDetails({
          sessionId,
          status: 'completed',
          contentTitle: magicData.contentTitle,
          hasDirectAccess: true
        });
      } else {
        // Final fallback - regular success page
        setPurchaseDetails({
          sessionId,
          status: 'completed'
        });
      }
    } catch (error) {
      console.error('Magic link fallback failed:', error);
      setPurchaseDetails({
        sessionId,
        status: 'completed'
      });
    } finally {
      setLoading(false);
      setAutoLoggingIn(false);
    }
  };

  const handleRetryAccess = async () => {
    if (retryAttempted) return;
    setRetryAttempted(true);
    setLoading(true);
    await handleAutoLogin(sessionId || '');
  };

  const getLoadingMessage = () => {
    if (!autoLoggingIn) return { title: 'Processing your purchase...', subtitle: 'Please wait' };
    
    switch (loadingStep) {
      case 1:
        return { title: '🎉 Account created!', subtitle: 'Setting up your access...' };
      case 2:
        return { title: '🔐 Logging you in...', subtitle: 'Preparing your session...' };
      case 3:
        return { title: '📚 Preparing your content...', subtitle: 'Almost ready!' };
      default:
        return { title: 'Setting up your account...', subtitle: 'You\'ll be redirected shortly' };
    }
  };

  if (loading) {
    const { title, subtitle } = getLoadingMessage();
    
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
          <div className="text-xl font-semibold text-gray-900 mb-2">
            {title}
          </div>
          <div className="text-sm text-gray-600 mb-4">
            {subtitle}
          </div>
          {autoLoggingIn && (
            <div className="flex justify-center space-x-2 mb-4">
              <div className={`w-3 h-3 rounded-full ${
                loadingStep >= 1 ? 'bg-green-500' : 'bg-gray-300'
              }`}></div>
              <div className={`w-3 h-3 rounded-full ${
                loadingStep >= 2 ? 'bg-green-500' : 'bg-gray-300'
              }`}></div>
              <div className={`w-3 h-3 rounded-full ${
                loadingStep >= 3 ? 'bg-green-500' : 'bg-gray-300'
              }`}></div>
            </div>
          )}
          <p className="text-xs text-gray-500">This usually takes just a few seconds</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Purchase Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a 
            href="/catalog" 
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Return to Catalog
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Purchase Successful!</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-green-500 text-6xl mb-6">✅</div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {purchaseDetails?.autoLogin ? 'Welcome to your content!' : 'Thank you for your purchase!'}
          </h2>
          
          {purchaseDetails?.autoLogin ? (
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                🎉 Success! You now have access to <strong>{purchaseDetails.contentTitle}</strong>
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-green-800 font-medium mb-2">✨ You're being redirected automatically...</p>
                <p className="text-green-700 text-sm">
                  Your account has been created and you're logged in. 
                  You'll be taken to your content library in a few seconds.
                </p>
              </div>
              <button
                onClick={() => {
                  if (purchaseDetails.purchaseType === 'content' && purchaseDetails.contentId) {
                    navigate(`/client/portal?tab=resources&viewContent=${purchaseDetails.contentId}`);
                  } else if (purchaseDetails.purchaseType === 'booking') {
                    navigate('/client/portal?tab=appointments');
                  } else {
                    navigate('/client/portal?tab=resources');
                  }
                }}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                {purchaseDetails.purchaseType === 'content' ? 'View My Content Now →' : 
                 purchaseDetails.purchaseType === 'booking' ? 'View My Appointments →' : 'Access My Content Now →'}
              </button>
            </div>
          ) : (
            <p className="text-gray-600 mb-6">
              Your payment has been processed successfully. You should receive an email shortly with:
            </p>
          )}

          {!purchaseDetails?.autoLogin && (
            <div className="space-y-4">
              {/* Direct Access Option */}
              {magicLinkUrl && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
                  <h4 className="text-green-900 font-semibold mb-3 flex items-center gap-2">
                    <span className="text-green-500">🚀</span>
                    Instant Access Available!
                  </h4>
                  <p className="text-green-700 mb-4 text-sm">
                    Your account is ready! Click below to access your content immediately:
                  </p>
                  <a
                    href={magicLinkUrl}
                    className="inline-block bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Access My Content Now →
                  </a>
                </div>
              )}
              
              {/* Retry Option */}
              {!magicLinkUrl && !retryAttempted && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-4">
                  <h4 className="text-yellow-900 font-semibold mb-3 flex items-center gap-2">
                    <span className="text-yellow-500">⚡</span>
                    Having trouble accessing your content?
                  </h4>
                  <p className="text-yellow-700 mb-4 text-sm">
                    We can try to set up immediate access for you:
                  </p>
                  <button
                    onClick={handleRetryAccess}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Try Instant Access
                  </button>
                </div>
              )}
              
              {/* Email Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h4 className="text-blue-900 font-semibold mb-3 flex items-center gap-2">
                  <span className="text-blue-500">📧</span>
                  Check Your Email
                </h4>
                <ul className="text-left space-y-2 text-blue-800">
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">🔗</span>
                    <span>A magic link to access your purchased content</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">🔐</span>
                    <span>Instructions for creating your account</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">📄</span>
                    <span>Direct download links for your content</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="text-sm text-gray-500">
              Session ID: {sessionId}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/catalog" 
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Browse More Content
              </a>
              <a 
                href="/intake" 
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Become a Full Client
              </a>
            </div>
          </div>

          {/* Next Steps */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">What's Next?</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-900 mb-2">Content Access</h4>
                <p className="text-green-700 text-sm">
                  Check your email for a magic link to access your purchased content. 
                  The link will create your account automatically.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                  <span className="text-purple-500">✨</span>
                  Love this content? Unlock more!
                </h4>
                <p className="text-purple-700 text-sm mb-3">
                  Get personalized consultations, exclusive resources, and one-on-one healing sessions.
                </p>
                <a 
                  href="/intake" 
                  className="inline-block bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all transform hover:scale-105"
                >
                  Begin Your Full Journey →
                </a>
              </div>
            </div>
          </div>

          {/* Support */}
          <div className="mt-6 text-sm text-gray-500">
            <p>
              Didn't receive your email? Check your spam folder or{' '}
              <a href="mailto:hailey@healingwithhailey.ca" className="text-blue-600 hover:text-blue-800">
                contact support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};